import React, { createContext, useContext, useEffect, useState } from 'react';

export const DEFAULT_FONT_SIZE = 100;
export const MIN_FONT_SIZE = 85;
export const MAX_FONT_SIZE = 115;
export const FONT_SIZE_STEP = 5;
const DEFAULT_BASE_FONT_SIZE_PX = 14;

const STORAGE_KEY = 'crm-font-size';

interface FontSizeContextProps {
  canDecrease: boolean;
  canIncrease: boolean;
  decreaseFontSize(): void;
  fontSize: number;
  increaseFontSize(): void;
  resetFontSize(): void;
  setFontSize: React.Dispatch<React.SetStateAction<number>>;
}

const FontSizeContext = createContext<FontSizeContextProps>({} as FontSizeContextProps);

type Props = {
  children?: React.ReactNode;
};

const clampFontSize = (value: number) => Math.min(MAX_FONT_SIZE, Math.max(MIN_FONT_SIZE, value));

const getInitialFontSize = () => {
  if (typeof window === 'undefined') {
    return DEFAULT_FONT_SIZE;
  }

  const storedFontSize = window.localStorage.getItem(STORAGE_KEY);
  const parsedFontSize = Number(storedFontSize);

  if (!storedFontSize || Number.isNaN(parsedFontSize)) {
    return DEFAULT_FONT_SIZE;
  }

  return clampFontSize(parsedFontSize);
};

export const FontSizeProvider: React.FC<Props> = ({ children }) => {
  const [fontSize, setFontSize] = useState<number>(getInitialFontSize);

  useEffect(() => {
    const nextFontSize = clampFontSize(fontSize);
    const nextRootFontSizePx = (DEFAULT_BASE_FONT_SIZE_PX * nextFontSize) / DEFAULT_FONT_SIZE;

    document.documentElement.style.fontSize = `${nextRootFontSizePx}px`;
    window.localStorage.setItem(STORAGE_KEY, String(nextFontSize));

    if (nextFontSize !== fontSize) {
      setFontSize(nextFontSize);
    }
  }, [fontSize]);

  const decreaseFontSize = () => {
    setFontSize(currentFontSize => clampFontSize(currentFontSize - FONT_SIZE_STEP));
  };

  const increaseFontSize = () => {
    setFontSize(currentFontSize => clampFontSize(currentFontSize + FONT_SIZE_STEP));
  };

  const resetFontSize = () => {
    setFontSize(DEFAULT_FONT_SIZE);
  };

  const value = {
    canDecrease: fontSize > MIN_FONT_SIZE,
    canIncrease: fontSize < MAX_FONT_SIZE,
    decreaseFontSize,
    fontSize,
    increaseFontSize,
    resetFontSize,
    setFontSize,
  };

  return <FontSizeContext.Provider value={value}>{children}</FontSizeContext.Provider>;
};

export const useFontSize = (): FontSizeContextProps => {
  return useContext(FontSizeContext);
};

export default FontSizeContext;
